import {Shape} from 'diagram-js/lib/model';
import {select, Store} from '@ngrx/store';
import {State} from '@ispm-client/modules/bpmn-modeler/store/state';
import {
    selectFloatedNodesByIdAndByLayer,
    selectFloatedNodesByLayer
} from '@ispm-client/modules/bpmn-modeler/store/selectors/floating-nodes.selector';
import {take} from 'rxjs/operators';
import {TreeNavigator} from '@ispm/shared/bpmn-io/services/tree-navigator';
import {ModelingUtil} from '@ispm/shared/bpmn-io/modeling-util';
import {END_NODE_ID, HEAD_NODE_ID} from '@ispm-shared/modules/bpmn/diagram-element-terms';
import {NodeType} from '@ispm/shared/bpmn-io/node-type';
import {EventType} from '@ispm/shared/bpmn-io/bpmn-js';
import EventBus from 'diagram-js/lib/core/EventBus';
import * as _ from 'lodash';
import {isEmpty} from 'lodash';
import {PathwaysModeling} from "@ispm-client/modules/bpmn-modeler/bpmn/commands/pathways-modeling";

export class FloatingNodesService {


    constructor(private store$: Store<State>, private treeNavigator: TreeNavigator, private eventBus: EventBus, private elementRegistry,protected pathwaysModeling: PathwaysModeling) {
    }

    // if parent is floated so the new node is floated too
    public addFloatedElementWhenAddingNewNode(source, newNode, layer): void {

        this.store$.pipe(select(selectFloatedNodesByIdAndByLayer(layer, source.id)), take(1)).subscribe(floatedNode => {
            if (floatedNode) {
                this.fireFloatedNodesAdded([newNode.id]);
            }
        });
    }

    public addFloatedElementWhenRemovingNodes(node): void {
        const floatedNodes = this.calculateFloatedNodesOnRemovingNode(node);
        if (!isEmpty(floatedNodes)) {
            const flattedFloatedNodes = floatedNodes.reduce((acc, val) => acc.concat(val), []);
            let floatedNodesIds = flattedFloatedNodes.map(floatedNode => floatedNode.id);
            floatedNodesIds = floatedNodesIds.filter(floatedNodeID => floatedNodeID !== END_NODE_ID);
            this.fireFloatedNodesAdded(floatedNodesIds);
        }
    }

    public addFloatedElementsWhenRemovingConnection(connection): void {
        const floatedNodes = this.calculateFloatedNodesOnRemovingConnection(connection);
        if (!isEmpty(floatedNodes)) {
            const flattedFloatedNodes = floatedNodes.reduce((acc, val) => acc.concat(val), []);
            let floatedNodesIds = flattedFloatedNodes.map(floatedNode => floatedNode.id);
            floatedNodesIds = floatedNodesIds.filter(floatedNodeID => floatedNodeID !== END_NODE_ID);
            this.fireFloatedNodesAdded(floatedNodesIds);
        }
    }

    public calculateFloatedSubtree(node): Array<Array<Shape>> {
        let floatedSubTree = [];
        if (ModelingUtil.isGateway(node)) {
            const outgoingConnections = node.outgoing;
            if (outgoingConnections && outgoingConnections.length > 0) {
                for (const conn of outgoingConnections) {
                    const target = conn.target;
                    floatedSubTree = [...floatedSubTree, this.calculateFloatedNode(target)];
                }
            }
        } else {
            floatedSubTree = this.calculateFloatedNode(node);
        }
        return floatedSubTree;
    }

    public calculateFloatedNode(node): Array<Shape> {
        let floatedNodes = [];
        if (!ModelingUtil.isGateway(node) && ModelingUtil.getNodeType(node) !== NodeType.neighborhood_head) {
          floatedNodes = this.calculateFloatedNodeRecursion(node);
        }
        return floatedNodes;
    }

  private calculateFloatedNodeRecursion(node): Array<Shape> {
    let floatedNodes = [];
    if (!ModelingUtil.hasDeletionTrackingChange(node)) {
      floatedNodes.push(node);
    }
    if (ModelingUtil.getNodeType(node) !== NodeType.neighborhood_entry) {
      let children = ModelingUtil.getDirectTreeChildrenNodes(node);
      if (children && children.length > 0) {
        children = children.filter(child => !ModelingUtil.hasDeletionTrackingChange(child) && !ModelingUtil.isGateway(child) && child.id !== END_NODE_ID);
        children.forEach((child) => {
          floatedNodes = [...floatedNodes, ...this.calculateFloatedNodeRecursion(child)];
        });
      }
    }
    return floatedNodes;
  }


    public removeFloatedSubTreeFromStoreAfterAppendConnection(node): void {
        if (ModelingUtil.isGateway(node)) {
            const outgoingConnections = node.outgoing;
            if (outgoingConnections && outgoingConnections.length > 0) {
                for (const conn of outgoingConnections) {
                    const target = conn.target;
                    this.removeFloatedElement(target);
                }
            }
        } else {
            this.removeFloatedElement(node);
        }
    }

    public nodeIsNotFloated(node): boolean {
        const parent = ModelingUtil.getNodeFirstAncestor(node);
        if (parent && parent.id) {
            // if the node is connected to headnode or neighborhood node or neighborhood entry so its not floating any more, and same for node children
            if (parent.id === HEAD_NODE_ID || ModelingUtil.getNodeType(parent) === NodeType.neighborhood_head || ModelingUtil.getNodeType(parent) === NodeType.neighborhood_entry) {
                return true;
            }

        }
        return false;
    }

  public removeFloatedElement(element, layer?, visited?): void {
      if (! visited) {
        visited = new Set();
      } else if (visited.has(element)) {
        return;
      }
      visited.add(element);
    const currentLayer = layer ? layer : this.getCurrentLayer();
    if (ModelingUtil.getNodeType(element) === NodeType.neighborhood_head) {
      this.store$.pipe(select(selectFloatedNodesByLayer(element.id)), take(1)).subscribe((floatedNodes) => {
        if (floatedNodes) {
          floatedNodes.forEach((floatedNodeID) => {
            this.removeFloatedElementFromStore(floatedNodeID, element, element.id, visited);
          })
        }
      });
    } else {
      this.store$.pipe(select(selectFloatedNodesByIdAndByLayer(currentLayer, element.id)), take(1)).subscribe((floatedNode) => {
        if (floatedNode) {
          this.removeFloatedElementFromStore(floatedNode, element, null, visited);
        }
      });
    }
  }

  private removeFloatedElementFromStore(floatedNodeId,element,layer?,visited?){
    let floatedNodesToRemoveFromStore = [];
    if (floatedNodeId && this.nodeIsNotFloated(element)) {
      floatedNodesToRemoveFromStore.push(floatedNodeId);
      const children = ModelingUtil.getNodeType(element) === NodeType.neighborhood_entry ? [] : ModelingUtil.getTreeChildren(element);
      if (children && children.length > 0) {
        children.forEach((child) => {
          this.removeFloatedElement(child, null, visited);
        })
      }
    }
    this.fireFloatedNodesRemoved(floatedNodesToRemoveFromStore,layer);
  }

  public removeFloatedElementsFromRegistryInNeighborhood(elements): void {
    const neighborhoodHeadNodes = elements.filter((element) => ModelingUtil.getNodeType(element) === NodeType.neighborhood_head);
    neighborhoodHeadNodes.forEach((neighborhoodHeadNode) => {
      this.store$.pipe(select(selectFloatedNodesByLayer(neighborhoodHeadNode.id)), take(1)).subscribe(floatedNodeIDs => {
        if (!_.isEmpty(floatedNodeIDs)) {
          let floatedNodesToRemoveFromStore = [];
          floatedNodeIDs.forEach((floatedNodeId) => {
            const floatedNodeElement = this.elementRegistry.get(floatedNodeId);
            if (floatedNodeElement) {
              this.pathwaysModeling.removeNode(floatedNodeElement,false,true);
              floatedNodesToRemoveFromStore.push(floatedNodeId);
            }
          });
          this.fireFloatedNodesRemoved(floatedNodesToRemoveFromStore,neighborhoodHeadNode.id);
        }
      });
    });
  }

    private fireFloatedNodesAdded(nodesIDs: Array<string>) {
      this.eventBus.fire(EventType.FLOATED_NODES_ADDED, {
            nodes: nodesIDs,
            layer: this.getCurrentLayer()
        });
    }

  private fireFloatedNodesRemoved(nodesIDs: Array<string>, layer?: string) {
    const layerID = layer ? layer : this.getCurrentLayer();
    this.eventBus.fire(EventType.FLOATED_NODES_REMOVED, {
      layer: layerID,
      nodes: nodesIDs
    });
  }

    private calculateFloatedNodesOnRemovingConnection(connection): Array<Shape> {
        if (ModelingUtil.isConnection(connection)) {
            const target = connection.target;
            return this.calculateFloatedSubtree(target);
        }
        return [];
    }

    private calculateFloatedNodesOnRemovingNode(node): Array<Shape> {

        if (node.outgoing && node.outgoing.length > 0 && node.outgoing[0].target.id !== END_NODE_ID) {
          const target =node.outgoing[0].target;
          return this.calculateFloatedSubtree(target);
        }
        return [];
    }

    private getCurrentLayer(): string {
        return this.treeNavigator.currentLayer;
    }
}
